import { useEffect, useState } from "react";
import api from "../lib/api";
import { useContent } from "../context/ContentContext";
import { ShoppingBag, Check, ChevronLeft, ChevronRight, ExternalLink, Package } from "lucide-react";

function ImageCarousel({ images, alt, testid }) {
    const [idx, setIdx] = useState(0);
    if (!images || images.length === 0) {
        return (
            <div className="aspect-[4/5] bg-meraki-surface border border-meraki-gold/15 flex items-center justify-center">
                <Package className="w-16 h-16 text-meraki-gold/30" />
            </div>
        );
    }
    const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);
    const next = () => setIdx((i) => (i + 1) % images.length);
    return (
        <div className="relative aspect-[4/5] bg-black border border-meraki-gold/20 overflow-hidden group" data-testid={testid}>
            <img src={images[idx]} alt={alt} className="w-full h-full object-cover" />
            {images.length > 1 && (
                <>
                    <button
                        onClick={prev}
                        aria-label="Previous image"
                        className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/70 hover:bg-meraki-gold hover:text-black text-meraki-gold border border-meraki-gold/40 p-2 transition-colors opacity-0 group-hover:opacity-100"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                        onClick={next}
                        aria-label="Next image"
                        className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/70 hover:bg-meraki-gold hover:text-black text-meraki-gold border border-meraki-gold/40 p-2 transition-colors opacity-0 group-hover:opacity-100"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                        {images.map((src, i) => (
                            <button
                                key={src || `dot-${i}`}
                                onClick={() => setIdx(i)}
                                aria-label={`Show image ${i + 1}`}
                                className={`w-2 h-2 rounded-full transition-colors ${
                                    i === idx ? "bg-meraki-gold" : "bg-meraki-gold/30"
                                }`}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

function ArticleCard({ article, index }) {
    const reverse = index % 2 === 1;
    const hasOrderUrl = article.order_url && article.order_url.trim().length > 0;
    const buttonLabel = article.order_button_label || "Order Now";

    return (
        <div
            data-testid={`article-${article.id}`}
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-stretch border border-meraki-gold/20 bg-meraki-surface p-6 md:p-10"
        >
            <div className={`lg:col-span-5 ${reverse ? "lg:order-2" : ""}`}>
                <ImageCarousel images={article.images} alt={article.name} testid={`article-images-${article.id}`} />
            </div>

            <div className={`lg:col-span-7 flex flex-col ${reverse ? "lg:order-1" : ""}`}>
                <div className="text-[10px] uppercase tracking-[0.4em] text-meraki-gold mb-3">Article {String(index + 1).padStart(2, "0")}</div>
                <h3 className="font-display text-3xl md:text-4xl lg:text-5xl uppercase text-meraki-text leading-[0.95]">{article.name}</h3>

                {article.description && (
                    <p className="mt-5 text-meraki-text/80 leading-relaxed whitespace-pre-line">{article.description}</p>
                )}

                {article.items && article.items.length > 0 && (
                    <div className="mt-6">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-meraki-gold mb-3">What's Included</div>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {article.items.map((item, i) => {
                                const name = typeof item === "string" ? item : item?.name;
                                const count = typeof item === "object" ? item?.count : null;
                                if (!name) return null;
                                return (
                                    <li key={`${name}-${i}`} className="flex items-start gap-2 text-sm text-meraki-text/85">
                                        <Check className="w-4 h-4 text-meraki-gold shrink-0 mt-0.5" />
                                        <span className="flex-1">
                                            {name}
                                            {count != null && count !== "" && (
                                                <span className="ml-2 text-meraki-gold font-mono text-xs">× {count}</span>
                                            )}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}

                {article.sizes && article.sizes.length > 0 && (
                    <div className="mt-6">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-meraki-gold mb-3">Sizes Available</div>
                        <div className="flex flex-wrap gap-2">
                            {article.sizes.map((s, i) => {
                                const label = typeof s === "string" ? s : s?.label;
                                const count = typeof s === "object" ? s?.count : null;
                                if (!label) return null;
                                const outOfStock = count === 0;
                                return (
                                    <div
                                        key={`${label}-${i}`}
                                        className={`flex items-center gap-2 border px-3 py-2 ${
                                            outOfStock
                                                ? "border-meraki-muted/30 text-meraki-muted/70 line-through"
                                                : "border-meraki-gold/40 text-meraki-text"
                                        }`}
                                    >
                                        <span className="font-display text-lg leading-none uppercase tracking-wider">{label}</span>
                                        {count != null && count !== "" && (
                                            <span className="text-[10px] uppercase tracking-[0.2em] text-meraki-gold">
                                                {outOfStock ? "Sold out" : `${count} left`}
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {article.conditions && article.conditions.length > 0 && (
                    <div className="mt-6 flex flex-wrap gap-2">
                        {article.conditions.map((cond, i) => (
                            <span
                                key={`${cond}-${i}`}
                                className="text-[10px] uppercase tracking-[0.2em] text-meraki-gold border border-meraki-gold/40 px-3 py-1.5"
                            >
                                {cond}
                            </span>
                        ))}
                    </div>
                )}

                <div className="mt-auto pt-8 flex flex-col sm:flex-row sm:items-end justify-between gap-5 border-t border-meraki-gold/15">
                    {article.final_price && (
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-meraki-muted">Final Price</div>
                            <div className="font-display text-4xl md:text-5xl text-meraki-gold leading-none mt-1">{article.final_price}</div>
                        </div>
                    )}
                    {hasOrderUrl ? (
                        <a
                            href={article.order_url}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`article-order-btn-${article.id}`}
                            className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-meraki-gold text-black text-xs uppercase tracking-[0.25em] font-bold hover:bg-meraki-gold-hover transition-all hover:-translate-y-0.5"
                        >
                            {buttonLabel}
                            <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                    ) : (
                        <span className="inline-flex items-center justify-center gap-2 px-7 py-4 border border-meraki-gold/30 text-meraki-muted text-xs uppercase tracking-[0.25em]">
                            Coming Soon
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function Merchandise() {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const { content } = useContent();
    const m = content.merch;

    useEffect(() => {
        api.get("/articles").then((r) => setArticles(r.data || [])).catch(() => {}).finally(() => setLoading(false));
    }, []);

    // Hide section if loading or no active articles — keeps page clean
    if (!loading && articles.length === 0) {
        return null;
    }

    return (
        <section id="merch" data-testid="merch-section" className="relative py-24 md:py-40 bg-meraki-bg">
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
                <div className="flex items-center gap-3 mb-6">
                    <span className="font-mono text-meraki-gold text-sm">05</span>
                    <div className="w-8 h-px bg-meraki-gold" />
                    <span className="text-[10px] uppercase tracking-[0.4em] text-meraki-gold">{m.eyebrow}</span>
                </div>
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
                    <h2 className="font-display text-5xl md:text-6xl lg:text-7xl uppercase leading-[0.95] text-meraki-text">
                        {m.heading_line1}<br /><span className="text-meraki-gold">{m.heading_line2}</span>
                    </h2>
                    <div className="flex items-center gap-3 max-w-md">
                        <ShoppingBag className="w-5 h-5 text-meraki-gold shrink-0" />
                        <p className="text-meraki-muted leading-relaxed whitespace-pre-line">{m.paragraph}</p>
                    </div>
                </div>

                {loading ? (
                    <div className="text-meraki-muted text-sm">Loading…</div>
                ) : (
                    <div className="space-y-10">
                        {articles.map((a, i) => (
                            <ArticleCard key={a.id} article={a} index={i} />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}
